import * as React from 'react';
import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import { useSelector } from 'react-redux';

import { trackDeveloperFlatsLinkClick, trackOffersBlockShown, trackOffersToLayoutsSwitcherClick } from './tracking';
import { OffersBlock } from '../../components/OffersBlock';
import { OffersHeader } from '../../components/OffersHeader';
import { OffersMarks } from '../../components/OffersMarks';

import { OffersToLayoutsSwitcher } from '../../components/OffersToLayoutsSwitcher';
import { getBuildingHasLayouts, getDisplayName } from '../../selectors/newbuilding';
import { getOffers } from '../../selectors/offers';

import { EscrowBlockContainer } from '../EscrowBlockContainer';
import { HousesFilterContainer } from '../HousesFilter';
import { LayoutsContainer } from '../LayoutsContainer';
import { LayoutsLinkContainer } from '../LayoutsLink';
import { OffersContainer } from '../Offers';
import { PriceRelevance } from '../PriceRelevance';
import { ReliableBlockContainer } from '../ReliableBlock';
import { SalesStartInfoContainer } from '../SalesStartInfo';

export function OffersBlockContainer() {
  const { fromDeveloperPropsCountDisplay, fromDeveloperPropsCountUrl, fromAgentsPropsCountDisplay } =
    useSelector(getOffers);
  const newbuildingName = useSelector(getDisplayName);
  const hasBuildingLayouts = useSelector(getBuildingHasLayouts);
  const [isLayoutsVisible, setIsLayoutsVisible] = React.useState(false);

  const onChange = React.useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, value: boolean) => {
      setIsLayoutsVisible(value);
      trackOffersToLayoutsSwitcherClick({ isActive: value });
    },
    [setIsLayoutsVisible],
  );

  React.useEffect(() => {
    if (!hasBuildingLayouts) {
      return;
    }

    if (window.location.hash === '#layouts') {
      setIsLayoutsVisible(true);
    }
  }, [hasBuildingLayouts]);

  if (!fromAgentsPropsCountDisplay && !fromDeveloperPropsCountDisplay) {
    return null;
  }

  return (
    <ActionAfterViewObserver callback={trackOffersBlockShown}>
      <OffersBlock
        title={
          <OffersHeader
            fromDeveloperPropsCountDisplay={fromDeveloperPropsCountDisplay}
            fromDeveloperPropsCountUrl={fromDeveloperPropsCountUrl}
            newbuildingName={newbuildingName}
            onLinkClick={trackDeveloperFlatsLinkClick}
            salesStartInfo={<SalesStartInfoContainer />}
          />
        }
        priceRelevance={<PriceRelevance />}
        offers={
          <>
            <HousesFilterContainer />
            {hasBuildingLayouts && <OffersToLayoutsSwitcher checked={isLayoutsVisible} onChange={onChange} />}
            {!isLayoutsVisible && <OffersContainer />}
            {isLayoutsVisible && <LayoutsContainer />}
            <LayoutsLinkContainer />
            <OffersMarks>
              <ReliableBlockContainer />
              <EscrowBlockContainer />
            </OffersMarks>
          </>
        }
      />
    </ActionAfterViewObserver>
  );
}
